import axios from "axios";
import config from "../Config";

const STORAGE_KEY = "hw_marketing_attribution";
const VISITOR_ID_KEY = "hw_visitor_id";

const ATTRIBUTION_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "gclid",
  "fbclid",
];

/**
 * Safely read localStorage.
 */
const getStorageItem = (key) => {
  try {
    return localStorage.getItem(key);
  } catch (error) {
    console.error(`LocalStorage read failed for ${key}:`, error);
    return null;
  }
};

/**
 * Safely write localStorage.
 */
const setStorageItem = (key, value) => {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (error) {
    console.error(`LocalStorage write failed for ${key}:`, error);
    return false;
  }
};

/**
 * Get or create a unique visitor ID.
 *
 * This ID identifies the browser/device for marketing attribution.
 */
export const getVisitorId = () => {
  let visitorId = getStorageItem(VISITOR_ID_KEY);

  if (visitorId) {
    return visitorId;
  }

  try {
    if (
      typeof crypto !== "undefined" &&
      typeof crypto.randomUUID === "function"
    ) {
      visitorId = crypto.randomUUID();
    } else {
      visitorId = `${Date.now()}-${Math.random()
        .toString(36)
        .substring(2, 14)}`;
    }
  } catch (error) {
    visitorId = `${Date.now()}-${Math.random()
      .toString(36)
      .substring(2, 14)}`;
  }

  setStorageItem(VISITOR_ID_KEY, visitorId);

  return visitorId;
};

/**
 * Read saved marketing attribution.
 */
export const getMarketingAttribution = () => {
  try {
    const stored = getStorageItem(STORAGE_KEY);

    if (!stored) {
      return {};
    }

    const parsed = JSON.parse(stored);

    if (!parsed || typeof parsed !== "object") {
      return {};
    }

    return parsed;
  } catch (error) {
    console.error("Marketing attribution read failed:", error);

    return {};
  }
};

/**
 * Extract UTM / advertising parameters from the current URL.
 */
const getCurrentMarketingParams = () => {
  try {
    if (typeof window === "undefined") {
      return {};
    }

    const params = new URLSearchParams(window.location.search);

    const marketingParams = {};

    ATTRIBUTION_KEYS.forEach((key) => {
      const value = params.get(key);

      if (value) {
        marketingParams[key] = value;
      }
    });

    return marketingParams;
  } catch (error) {
    console.error("Marketing parameter extraction failed:", error);

    return {};
  }
};

/**
 * Capture marketing attribution.
 *
 * First touch:
 *   The original campaign/source that brought the visitor.
 *
 * Last touch:
 *   The most recent campaign/source that brought the visitor.
 *
 * The API is called only when there is useful attribution data
 * or when this is the visitor's first recorded visit.
 */
export const captureMarketingAttribution = async () => {
  try {
    if (typeof window === "undefined") {
      return {};
    }

    const visitorId = getVisitorId();
    const existing = getMarketingAttribution();
    const current = getCurrentMarketingParams();

    const now = new Date().toISOString();

    const hasCurrentCampaign = Object.keys(current).length > 0;

    /**
     * First visit / landing page.
     */
    const landingPage =
      existing.landing_page || window.location.href;

    /**
     * First-touch attribution.
     *
     * These values are never overwritten once captured.
     */
    const firstTouch = {
      ...(existing.first_touch || {}),
    };

    if (
      hasCurrentCampaign &&
      !firstTouch.utm_source &&
      !firstTouch.gclid &&
      !firstTouch.fbclid
    ) {
      Object.assign(firstTouch, current);
    }

    /**
     * Last-touch attribution.
     *
     * These values can change when the visitor comes back
     * through another campaign.
     */
    const lastTouch = hasCurrentCampaign
      ? {
          ...current,
        }
      : existing.last_touch || {};

    const attribution = {
      ...existing,

      visitorId,

      /**
       * First visit information.
       */
      landing_page: landingPage,
      first_visit: existing.first_visit || now,

      /**
       * Last visit information.
       */
      last_visit: now,

      /**
       * First-touch campaign.
       */
      first_touch: firstTouch,

      /**
       * Last-touch campaign.
       */
      last_touch: lastTouch,

      /**
       * Backward compatibility:
       * Keep the original flat UTM fields.
       *
       * If a new campaign exists, update these values.
       */
      ...(hasCurrentCampaign ? current : {}),
    };

    setStorageItem(
      STORAGE_KEY,
      JSON.stringify(attribution),
    );

    /**
     * Send to backend when:
     *
     * 1. We have a new campaign
     * OR
     * 2. This is the first recorded visit.
     */
    const shouldSync =
      hasCurrentCampaign ||
      !existing.visitorId ||
      !existing.first_visit;

    if (shouldSync) {
      try {
        await axios.post(
          `${config.apiBaseUrl}/${config.marketingAttribution}`,
          attribution,
          {
            headers: {
              "Content-Type": "application/json",
            },
          },
        );
      } catch (error) {
        /**
         * Do not lose local attribution if the API fails.
         */
        console.error(
          "Marketing attribution API sync failed:",
          error,
        );
      }
    }

    return attribution;
  } catch (error) {
    console.error(
      "Marketing attribution capture failed:",
      error,
    );

    return getMarketingAttribution();
  }
};

/**
 * Attach a logged-in/registered user to the visitor.
 *
 * This connects:
 *
 * visitorId
 *      ↓
 * marketing attribution
 *      ↓
 * userId
 *
 * Example:
 *
 * Google Ads visitor
 *      ↓
 * visits website
 *      ↓
 * visitorId created
 *      ↓
 * signs up
 *      ↓
 * userId attached
 *      ↓
 * backend knows campaign → user
 */
export const attachMarketingUser = async ({
  userId,
  email = null,
  contact = null,
}) => {
  if (!userId) {
    return null;
  }

  try {
    const visitorId = getVisitorId();
    const attribution = getMarketingAttribution();

    const response = await axios.post(
      `${config.apiBaseUrl}/${config.marketingAttributionUser}`,
      {
        visitorId,
        userId,
        email,
        contact,
        attribution,
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    return response.data;
  } catch (error) {
    console.error(
      "Marketing user attachment failed:",
      error,
    );

    return null;
  }
};

/**
 * Clear only marketing attribution.
 *
 * The visitor ID is intentionally preserved.
 *
 * This is important because clearing the visitor ID would
 * create a completely new visitor identity.
 */
export const clearMarketingAttribution = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error(
      "Marketing attribution clear failed:",
      error,
    );
  }
};

/**
 * Completely reset marketing visitor tracking.
 *
 * Use this only when you intentionally want to create
 * a new visitor identity.
 */
export const resetMarketingVisitor = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(VISITOR_ID_KEY);
  } catch (error) {
    console.error(
      "Marketing visitor reset failed:",
      error,
    );
  }
};